// The case pipeline as the server counts it: open matters per stage within
// one practice area, with a chip for each area that holds any. A stage
// tile is a fact, not a control; the one link is the number, to the list
// it counts.

import { Link } from "react-router-dom";
import { serverPracticeAreaOf } from "../../api/mock";
import { OTHER_AREA, areasWithMatters, practiceAreaLabel } from "../../data/practice-areas";
import { useFirmCounts } from "../../hooks/useFirmCounts";
import { stageLabel } from "../../lib/caseLabels";
import { fmtNum } from "../../lib/money";
import { SEP } from "../chrome/SepList";
import { Skeleton, SkeletonGroup } from "../chrome/Skeleton";

interface Props {
  /** A frontend area id, "other", or "all" for the first area with matters. */
  practiceFilter: string;
  setPracticeFilter: (id: string) => void;
}

type AreaChip = Omit<typeof OTHER_AREA, "to">;

export function PipelineByArea({ practiceFilter, setPracticeFilter }: Props) {
  const firm = useFirmCounts();

  // An API without the stages is an older one: keep loading, never show none.
  if (!firm?.pipelineByArea) {
    return (
      <div className="cc-card" style={{ padding: 18 }}>
        <SkeletonGroup label="Loading case pipeline">
          <Skeleton width="30%" height={16} />
          <Skeleton width="100%" height={64} radius={9} />
        </SkeletonGroup>
      </div>
    );
  }

  const counts = firm.byPracticeArea;
  const chips: AreaChip[] = areasWithMatters(counts);
  if ((counts.other ?? 0) > 0) chips.push(OTHER_AREA);

  const visible = practiceFilter === "all" ? chips[0] : chips.find((c) => c.id === practiceFilter);
  const stages = visible ? (firm.pipelineByArea[visible.id] ?? []) : [];
  const total = visible ? (counts[visible.id] ?? 0) : 0;
  // A named area the caseload does not hold is said so under its own name.
  const askedLabel = practiceAreaLabel(practiceFilter);

  return (
    <div className="cc-card cc-pipe-card">
      <div className="cc-card-head">
        <div className="cc-card-title" style={{ display: "flex", alignItems: "center", gap: 8 }}>
          {visible && <span className="pa-dot lg" style={{ background: visible.accent }} />}
          Case pipeline
        </div>
        {visible && (
          <div className="cc-card-sub">
            {SEP}
            {visible.label}
            {SEP}
            <Link to={`/cases?area=${serverPracticeAreaOf(visible.id)}`} className="cc-link-subtle">
              {fmtNum(total)} open {total === 1 ? "matter" : "matters"}
            </Link>
          </div>
        )}
        <div className="actions">
          {chips.map((c) => (
            <button
              key={c.id}
              type="button"
              className="cc-link-btn"
              aria-pressed={c.id === visible?.id}
              onClick={() => setPracticeFilter(c.id)}
              style={{ display: "inline-flex", alignItems: "center", gap: 5 }}
            >
              <span className="pa-dot sm" style={{ background: c.accent }} />
              {c.shortLabel}
            </button>
          ))}
        </div>
      </div>
      {visible ? (
        <div className="cc-pipeline">
          <div className="cc-pipe-row">
            {stages.map((s) => (
              <div key={s.stage} className="cc-pipe-stage">
                <div className="name">{stageLabel(s.stage)}</div>
                <div className="count">{fmtNum(s.count)}</div>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div style={{ padding: 18, color: "var(--muted)", fontSize: 12.5 }}>
          {chips.length === 0 ? "No open matters yet." : `No open matters in ${askedLabel}.`}
        </div>
      )}
    </div>
  );
}
